import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCw,
  Printer,
  Mail,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Plus,
  X,
  Users,
  Save,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const rand = (v) =>
  `R ${Number(v || 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const gpColor = (gp) => {
  if (gp >= 25) return "text-emerald-600";
  if (gp >= 15) return "text-amber-600";
  return "text-red-600";
};

const gpBg = (gp) => {
  if (gp >= 25) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (gp >= 15) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
};

function DeltaChip({ value, suffix = "%" }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>;
  }
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const cls =
    value > 0
      ? "text-emerald-600"
      : value < 0
      ? "text-red-600"
      : "text-gray-500";
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(value).toFixed(1)}
      {suffix}
    </span>
  );
}

function KpiTile({ label, value, color, testid }) {
  return (
    <div
      className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        {label}
      </div>
      <div className={`text-lg font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

export default function DayEndReport() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Africa/Johannesburg",
  });
  const [date, setDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [lastSend, setLastSend] = useState(null);

  // Recipient management for the automated 19:00 SAST email.
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoaded, setRecipientsLoaded] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [recipientsDirty, setRecipientsDirty] = useState(false);

  const fetchReport = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/reports/day-end?date=${d}`, {
        timeout: 60000,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(date);
  }, [date, fetchReport]);

  // Fetch last-send marker once on mount so the user can see whether the
  // 19:00 SAST cron actually fired.
  useEffect(() => {
    axios
      .get(`${API}/api/reports/day-end/last-send`, { timeout: 10000 })
      .then((r) => setLastSend(r.data?.sent_at ? r.data : null))
      .catch(() => {});
  }, []);

  // Load the configured recipient list for the automated 19:00 SAST email.
  useEffect(() => {
    axios
      .get(`${API}/api/reports/day-end/recipients`, { timeout: 10000 })
      .then((r) => {
        setRecipients(Array.isArray(r.data?.recipients) ? r.data.recipients : []);
        setRecipientsLoaded(true);
      })
      .catch(() => setRecipientsLoaded(true));
  }, []);

  const addRecipient = () => {
    const addr = (newRecipient || "").trim();
    if (!addr) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
      toast.error("That doesn't look like a valid email address.");
      return;
    }
    if (recipients.some((r) => r.toLowerCase() === addr.toLowerCase())) {
      toast.info("That email is already on the list.");
      return;
    }
    setRecipients([...recipients, addr]);
    setNewRecipient("");
    setRecipientsDirty(true);
  };

  const removeRecipient = (addr) => {
    setRecipients(recipients.filter((r) => r !== addr));
    setRecipientsDirty(true);
  };

  const saveRecipients = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one email address before saving.");
      return;
    }
    setSavingRecipients(true);
    try {
      const r = await axios.put(
        `${API}/api/reports/day-end/recipients`,
        { recipients },
        { timeout: 15000 }
      );
      setRecipients(r.data?.recipients || recipients);
      setRecipientsDirty(false);
      toast.success("Recipient list saved. The 19:00 SAST email will use this list from now on.");
    } catch (err) {
      const raw = err.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : err.message || "Failed to save recipients");
    } finally {
      setSavingRecipients(false);
    }
  };

  const handleSendEmail = async () => {
    if (!data) return;
    setSending(true);
    const toastId = toast.loading("Sending End-of-Day report…");
    try {
      const res = await axios.post(
        `${API}/api/reports/day-end/send-email?date=${date}`,
        {},
        { timeout: 60000 }
      );
      toast.dismiss(toastId);
      toast.success(res.data?.message || "Email sent");
      // Refresh last-send marker so the user immediately sees the new timestamp.
      axios
        .get(`${API}/api/reports/day-end/last-send`, { timeout: 10000 })
        .then((r) => setLastSend(r.data?.sent_at ? r.data : null))
        .catch(() => {});
    } catch (err) {
      toast.dismiss(toastId);
      // FastAPI validation errors return an array — coerce to a safe string
      // so sonner never receives a non-string child (which would blank the
      // page).
      const raw = err.response?.data?.detail;
      let msg;
      if (typeof raw === "string") {
        msg = raw;
      } else if (Array.isArray(raw)) {
        msg = raw.map((x) => x?.msg || JSON.stringify(x)).join("; ");
      } else if (raw && typeof raw === "object") {
        msg = JSON.stringify(raw);
      } else {
        msg = err.message || "Email failed";
      }
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const formattedDate = data?.date
    ? new Date(data.date + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

  const t = data?.totals || {};
  const cmp = data?.comparison || {};

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white" data-testid="day-end-report-page">
      {/* Header — hidden in print */}
      <div className="bg-white border-b shadow-sm print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/reports")}
              data-testid="back-to-reports-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Reports
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">End of Day Report</h1>
              <p className="text-[11px] text-gray-500">
                A one-page snapshot · auto-emailed every day at 19:00 SAST
                {lastSend?.sent_at && (
                  <>
                    {" · "}
                    <span data-testid="day-end-last-send">
                      last sent{" "}
                      {new Date(lastSend.sent_at).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      ({lastSend.date})
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={today}
              className="w-44 h-9"
              data-testid="day-end-date-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchReport(date)}
              disabled={loading}
              data-testid="day-end-refresh-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              data-testid="day-end-print-btn"
            >
              <Printer className="h-4 w-4 mr-1" />
              Print A4
            </Button>
            <Button
              size="sm"
              onClick={handleSendEmail}
              disabled={sending || !data}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="day-end-send-email-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-1" />
              )}
              Send Now
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 print:px-0 print:py-0">
        {/* Recipient management for the automated 19:00 SAST email.
            Hidden in print output so the printed sheet stays clean. */}
        <Card className="mb-4 border-blue-100 bg-blue-50/40 print:hidden" data-testid="day-end-recipients-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-blue-800 font-semibold">
                <Users className="h-4 w-4" />
                <span>Automated 19:00 SAST recipients</span>
              </div>
              <div className="text-[11px] text-blue-700/80 flex-1 min-w-[220px]">
                The report is emailed <b>once</b> per trading day (Sundays are skipped — the store is closed).
                Add any addresses that should also receive it.
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="day-end-recipient-chips">
              {recipientsLoaded && recipients.length === 0 && (
                <div className="text-xs text-gray-500 italic">
                  No recipients configured — add at least one below.
                </div>
              )}
              {recipients.map((addr) => (
                <span
                  key={addr}
                  className="inline-flex items-center gap-1 bg-white border border-blue-200 rounded-full pl-3 pr-1 py-1 text-xs text-blue-900"
                  data-testid={`day-end-recipient-${addr}`}
                >
                  {addr}
                  <button
                    type="button"
                    onClick={() => removeRecipient(addr)}
                    className="rounded-full hover:bg-red-100 text-red-600 p-0.5"
                    aria-label={`Remove ${addr}`}
                    data-testid={`day-end-recipient-remove-${addr}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3 items-center">
              <Input
                type="email"
                placeholder="add another email address…"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                className="w-72 h-9"
                data-testid="day-end-new-recipient-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRecipient}
                disabled={!newRecipient.trim()}
                data-testid="day-end-add-recipient-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveRecipients}
                disabled={!recipientsDirty || savingRecipients}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="day-end-save-recipients-btn"
              >
                {savingRecipients ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save recipients
              </Button>
              {recipientsDirty && (
                <span className="text-[11px] text-amber-700 font-semibold">Unsaved changes</span>
              )}
            </div>
          </CardContent>
        </Card>

        {loading || !data ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
            Loading report…
          </div>
        ) : (
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 print:shadow-none print:border-none print:p-0"
            data-testid="day-end-report-body"
          >
            {/* Header inside the printed sheet */}
            <div className="flex items-end justify-between border-b pb-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">
                  End of Day Report
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mt-0.5">
                  {data.company_name}
                </h2>
                <div className="text-sm text-gray-600 mt-0.5">{formattedDate}</div>
              </div>
              <div className="text-[10px] text-gray-400">
                Generated {new Date().toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })} SAST
              </div>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-4 gap-2 mb-5" data-testid="day-end-kpi-tiles">
              <KpiTile label="Turnover (incl VAT)" value={rand(t.turnover_incl)} color="text-blue-700" testid="kpi-turnover" />
              <KpiTile label="Nett Sales (excl VAT)" value={rand(t.nett_sales_excl)} color="text-blue-600" testid="kpi-nett" />
              <KpiTile label="Gross Profit" value={rand(t.gross_profit)} color="text-emerald-600" testid="kpi-gp" />
              <KpiTile label="GP %" value={`${(t.gp_pct || 0).toFixed(1)}%`} color={gpColor(t.gp_pct || 0)} testid="kpi-gp-pct" />
              <KpiTile label="Cost of Goods" value={rand(t.cost_of_goods)} color="text-purple-700" testid="kpi-cogs" />
              <KpiTile label="Output VAT (15%)" value={rand(t.output_vat)} color="text-cyan-700" testid="kpi-vat" />
              <KpiTile label="Transactions" value={Number(t.transactions || 0).toLocaleString()} color="text-gray-700" testid="kpi-tx" />
              <KpiTile label="Product Lines" value={Number(t.product_lines || 0).toLocaleString()} color="text-gray-700" testid="kpi-lines" />
            </div>

            {/* AI commentary — one-line trading-day insight */}
            {data.ai_commentary && (
              <div
                className="mb-5 rounded-lg border-l-4 border-amber-500 bg-gradient-to-r from-amber-50 to-amber-100/60 px-4 py-3"
                data-testid="day-end-ai-commentary"
              >
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold mb-1">
                  ✨ Today's take
                </div>
                <div className="text-sm text-gray-900 leading-relaxed">
                  {data.ai_commentary}
                </div>
              </div>
            )}

            {/* Comparison */}
            {cmp.yesterday && (
              <div className="border rounded-lg overflow-hidden mb-5" data-testid="day-end-comparison">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="text-left py-2 px-3 font-semibold">vs</th>
                      <th className="text-right py-2 px-3 font-semibold">Sales</th>
                      <th className="text-right py-2 px-3 font-semibold">Gross Profit</th>
                      <th className="text-right py-2 px-3 font-semibold">GP %</th>
                      <th className="text-right py-2 px-3 font-semibold">Transactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: "Yesterday", row: cmp.yesterday },
                      { label: "Same day last week", row: cmp.same_dow_last_week },
                    ].map(({ label, row }) => (
                      <tr key={label} className="border-t">
                        <td className="py-2 px-3 text-gray-800">
                          {label} <span className="text-gray-400">({row?.date})</span>
                        </td>
                        <td className="py-2 px-3 text-right"><DeltaChip value={row?.delta_sales_pct} /></td>
                        <td className="py-2 px-3 text-right"><DeltaChip value={row?.delta_profit_pct} /></td>
                        <td className="py-2 px-3 text-right"><DeltaChip value={row?.delta_gp_pct_points} suffix="pp" /></td>
                        <td className="py-2 px-3 text-right"><DeltaChip value={row?.delta_transactions_pct} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Till Banking */}
            <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-2">Till Banking Summary</h3>

            {/* Anomalies banner — dropped corrupted amounts (if any) */}
            {Array.isArray(data.anomalies) && data.anomalies.length > 0 && (
              <div
                className="border border-amber-300 bg-amber-50 rounded-lg p-3 mb-3 text-xs"
                data-testid="day-end-anomalies"
              >
                <p className="font-semibold text-amber-800 mb-1">
                  ⚠ {data.anomalies.length} corrupted amount(s) were excluded from the totals
                </p>
                <ul className="list-disc list-inside text-amber-700 space-y-0.5">
                  {data.anomalies.slice(0, 5).map((a, i) => (
                    <li key={i}>
                      <span className="font-mono">{a.context}</span> — raw value{" "}
                      <span className="font-mono">{a.raw_value?.toLocaleString?.() ?? a.raw_value}</span>
                    </li>
                  ))}
                  {data.anomalies.length > 5 && (
                    <li className="italic">+ {data.anomalies.length - 5} more (see backend logs)</li>
                  )}
                </ul>
              </div>
            )}

            <div className="border rounded-lg overflow-hidden mb-5" data-testid="day-end-till-banking">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-600">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold">Cashier</th>
                    <th className="text-right py-2 px-3 font-semibold">Cash</th>
                    <th className="text-right py-2 px-3 font-semibold">Card</th>
                    <th className="text-right py-2 px-3 font-semibold">EFT</th>
                    <th className="text-right py-2 px-3 font-semibold">Account</th>
                    <th className="text-right py-2 px-3 font-semibold">Payouts</th>
                    <th className="text-right py-2 px-3 font-semibold">Turnover</th>
                    <th className="text-right py-2 px-3 font-semibold">Net Banked</th>
                  </tr>
                </thead>
                <tbody>
                  {data.till_banking.rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-3 text-center text-gray-400">
                        No till activity for this day.
                      </td>
                    </tr>
                  ) : (
                    data.till_banking.rows.map((r) => (
                      <tr key={r.cashier} className="border-t">
                        <td className="py-2 px-3 text-gray-800">{r.cashier}</td>
                        <td className="py-2 px-3 text-right">{rand(r.cash)}</td>
                        <td className="py-2 px-3 text-right">{rand(r.card)}</td>
                        <td className="py-2 px-3 text-right">{rand(r.eft)}</td>
                        <td className="py-2 px-3 text-right">{rand(r.account)}</td>
                        <td className="py-2 px-3 text-right text-red-600">{rand(r.payouts)}</td>
                        <td className="py-2 px-3 text-right">{rand(r.turnover)}</td>
                        <td className="py-2 px-3 text-right text-emerald-700 font-semibold">
                          {rand(r.net_banked)}
                        </td>
                      </tr>
                    ))
                  )}
                  <tr className="border-t bg-gray-50 font-bold">
                    <td className="py-2 px-3">TOTALS</td>
                    <td className="py-2 px-3 text-right">{rand(data.till_banking.totals.cash)}</td>
                    <td className="py-2 px-3 text-right">{rand(data.till_banking.totals.card)}</td>
                    <td className="py-2 px-3 text-right">{rand(data.till_banking.totals.eft)}</td>
                    <td className="py-2 px-3 text-right">{rand(data.till_banking.totals.account)}</td>
                    <td className="py-2 px-3 text-right text-red-600">{rand(data.till_banking.totals.payouts)}</td>
                    <td className="py-2 px-3 text-right">{rand(data.till_banking.totals.turnover)}</td>
                    <td className="py-2 px-3 text-right text-emerald-700">{rand(data.till_banking.totals.net_banked)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Categories */}
            <h3 className="text-sm font-semibold text-gray-900 mt-5 mb-2">
              Product Performance by Category
            </h3>
            {data.by_category.length === 0 ? (
              <p className="text-xs text-gray-400 py-4">No sales for this day.</p>
            ) : (
              data.by_category.map((cat) => (
                <div
                  key={cat.category}
                  className="mb-4 print:break-inside-avoid"
                  data-testid={`day-end-cat-${cat.category}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {cat.category}
                      <span className="ml-2 font-normal text-[11px] text-gray-500">
                        {rand(cat.subtotal_incl)} incl · GP{" "}
                        <span className={gpColor(cat.gp_pct)}>{cat.gp_pct.toFixed(1)}%</span> ·{" "}
                        {cat.pct_of_day.toFixed(1)}% of day
                      </span>
                    </h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${gpBg(cat.gp_pct)}`}>
                      GP {cat.gp_pct.toFixed(1)}%
                    </span>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-100 text-gray-600">
                        <tr>
                          <th className="text-left py-1.5 px-2 font-semibold">Stock Item</th>
                          <th className="text-left py-1.5 px-2 font-semibold">PLU</th>
                          <th className="text-right py-1.5 px-2 font-semibold">Qty</th>
                          <th className="text-right py-1.5 px-2 font-semibold">Sell incl</th>
                          <th className="text-right py-1.5 px-2 font-semibold">Sell excl</th>
                          <th className="text-right py-1.5 px-2 font-semibold">Cost</th>
                          <th className="text-right py-1.5 px-2 font-semibold">Profit</th>
                          <th className="text-right py-1.5 px-2 font-semibold">GP %</th>
                          <th className="text-right py-1.5 px-2 font-semibold">% Grp</th>
                          <th className="text-right py-1.5 px-2 font-semibold">% Day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.items.map((item, idx) => (
                          <tr key={`${item.plu}-${idx}`} className="border-t">
                            <td className="py-1.5 px-2 text-gray-800 truncate max-w-[200px]">
                              {item.stock_item}
                            </td>
                            <td className="py-1.5 px-2 text-gray-500">{item.plu}</td>
                            <td className="py-1.5 px-2 text-right">{item.qty}</td>
                            <td className="py-1.5 px-2 text-right">{rand(item.sell_incl)}</td>
                            <td className="py-1.5 px-2 text-right">{rand(item.sell_excl)}</td>
                            <td className="py-1.5 px-2 text-right">{rand(item.cost)}</td>
                            <td className="py-1.5 px-2 text-right">{rand(item.profit)}</td>
                            <td className={`py-1.5 px-2 text-right font-semibold ${gpColor(item.gp_pct)}`}>
                              {item.gp_pct.toFixed(1)}%
                            </td>
                            <td className="py-1.5 px-2 text-right text-gray-500">
                              {item.pct_of_group.toFixed(1)}%
                            </td>
                            <td className="py-1.5 px-2 text-right text-gray-500">
                              {item.pct_of_day.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t bg-gray-50 font-bold">
                          <td colSpan={2} className="py-1.5 px-2">
                            {cat.category} Subtotal
                          </td>
                          <td className="py-1.5 px-2 text-right">{cat.qty}</td>
                          <td className="py-1.5 px-2 text-right">{rand(cat.subtotal_incl)}</td>
                          <td className="py-1.5 px-2 text-right">{rand(cat.subtotal_excl)}</td>
                          <td className="py-1.5 px-2 text-right">{rand(cat.cost)}</td>
                          <td className="py-1.5 px-2 text-right">{rand(cat.profit)}</td>
                          <td className={`py-1.5 px-2 text-right ${gpColor(cat.gp_pct)}`}>
                            {cat.gp_pct.toFixed(1)}%
                          </td>
                          <td className="py-1.5 px-2 text-right text-gray-400">—</td>
                          <td className="py-1.5 px-2 text-right">{cat.pct_of_day.toFixed(1)}%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}

            {/* Day Totals strip */}
            <div
              className="mt-6 rounded-lg bg-slate-900 text-white grid grid-cols-5 gap-2 px-4 py-3 print:break-inside-avoid"
              data-testid="day-end-totals-strip"
            >
              {[
                { label: "SALES INCL", val: rand(data.day_totals.sales_incl), color: "text-white" },
                { label: "SALES EXCL", val: rand(data.day_totals.sales_excl), color: "text-white" },
                {
                  label: "TOTAL PROFIT",
                  val: rand(data.day_totals.total_profit),
                  color: "text-emerald-300",
                },
                {
                  label: "GP %",
                  val: `${data.day_totals.gp_pct.toFixed(1)}%`,
                  color: gpColor(data.day_totals.gp_pct).replace("text-", "text-"),
                },
                { label: "OUTPUT VAT", val: rand(data.day_totals.output_vat), color: "text-white" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-[10px] text-slate-300 tracking-widest font-semibold">
                    {s.label}
                  </div>
                  <div className={`text-base font-bold mt-0.5 ${s.color}`}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* A4 print styling */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
